import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { differenceInSeconds } from '@repo/date-time';
import { useNavigate } from 'react-router-dom';
import { useInvalidateReservations, useReservationsLive, useVehicles } from '@repo/domain-service';
import { TestId } from '@repo/test-id';

import CreateVehicleContents from '@features/reservation/components/vehicle/CreateVehicleContents';
import SelectVehicleContents from '@features/reservation/components/vehicle/SelectVehicleContents';
import ReservationSummaryContents from '@features/reservation/components/summary/ReservationSummaryContents';
import ExitOverlay from '@features/reservation/components/exit/ExitOverlay';
import PendingReservationExistsModal from '@features/reservation/components/PendingReservationExistsModal';
import { useEnsurePendingReservation } from '@features/reservation/hooks/useEnsurePendingReservation';

import PleaseWaitAnimation from '@shared/components/PleaseWaitAnimation';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

export default function ReservationFlowPage() {
  const navigate = useNavigate();
  const testId = TestId.root('reservation.flow');
  const invalidateReservations = useInvalidateReservations();
  const { reservations, loadingReservations } = useReservationsLive(
    { only: 'pending' },
    { withParking: true, withVehicle: true, withSpot: true }
  );
  const { vehicles, loadingVehicles } = useVehicles();
  const [vehicleSelectionOpen, setVehicleSelectionOpen] = useState<boolean>(false);
  const pendingReservation = reservations[0];

  const { ensuring, blocked, continuing, showExisting, continueAnyway } = useEnsurePendingReservation();

  const hadReservationRef = useRef<boolean>(false);
  useEffect(() => {
    if (blocked || continuing) return;
    if (pendingReservation) {
      hadReservationRef.current = true;
      return;
    }
    if (hadReservationRef.current && !ensuring && !loadingReservations) {
      navigate(ROUTE_PATHS[RouteName.MAP], { replace: true });
    }
  }, [pendingReservation, ensuring, blocked, continuing, loadingReservations, navigate]);

  const secondsToDeadline = pendingReservation?.expiresAt
    ? Math.max(0, differenceInSeconds(pendingReservation.expiresAt, new Date()))
    : undefined;

  const handleExpire = () => {
    invalidateReservations();
    navigate(ROUTE_PATHS[RouteName.MAP], { replace: true });
  };

  const handleChangeVehicle = () => {
    setVehicleSelectionOpen(true);
  };

  const renderContent = (): ReactNode => {
    if (blocked) return null;

    if (loadingReservations || ensuring) return <PleaseWaitAnimation testId={testId} />;

    if (!pendingReservation) return null;

    if (!pendingReservation.vehicle?.registrationNumber) {
      if (loadingVehicles) return <PleaseWaitAnimation testId={testId} />;

      return vehicles.length ? (
        <SelectVehicleContents
          reservationId={pendingReservation.id}
          secondsToDeadline={secondsToDeadline}
          onExpire={handleExpire}
        />
      ) : (
        <CreateVehicleContents secondsToDeadline={secondsToDeadline} onExpire={handleExpire} />
      );
    }

    return (
      <ReservationSummaryContents
        reservation={pendingReservation}
        vehicleSelectionOpen={vehicleSelectionOpen}
        onVehicleSelectionOpenChange={setVehicleSelectionOpen}
        secondsToDeadline={secondsToDeadline}
        onExpire={handleExpire}
      />
    );
  };

  return (
    <>
      {renderContent()}
      {blocked && (
        <PendingReservationExistsModal
          testId={testId.append('pendingExistsModal')}
          continuing={continuing}
          onShowExisting={showExisting}
          onContinueAnyway={continueAnyway}
        />
      )}
      <ExitOverlay
        vehicleChangePossible={!!pendingReservation?.vehicle?.registrationNumber}
        isResolving={ensuring}
        reservationId={pendingReservation?.id}
        onChangeVehicle={handleChangeVehicle}
      />
    </>
  );
}
